import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  GlobeAltIcon as Globe,
  CheckIcon as Check,
} from "@heroicons/react/24/outline";

interface LanguageSelectionModalProps {
  isOpen: boolean;
  onLanguageSelect: (language: string) => void;
  onSkip: () => void;
}

const LanguageSelectionModal: React.FC<LanguageSelectionModalProps> = ({
  isOpen,
  onLanguageSelect,
  onSkip,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    i18n.language || "en"
  );
  const [isLoading, setIsLoading] = useState(false);

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
  ];

  const handleLanguageSelect = (langCode: string) => {
    setSelectedLanguage(langCode);
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      await i18n.changeLanguage(selectedLanguage);
      localStorage.setItem("vett-language-preference", selectedLanguage);
      localStorage.setItem("i18nextLng", selectedLanguage);
      onLanguageSelect(selectedLanguage);
    } catch (error) {
      console.error("Error changing language:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem("vett-language-preference", "skipped");
    onSkip();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-vett-turquoise to-vett-dark-green p-6 text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Globe className="w-8 h-8" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center mb-2">
              {t("languageSelection.title")}
            </h2>
            <p className="text-center text-white/90 text-sm">
              {t("languageSelection.subtitle")}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-6 text-center">
              {t("languageSelection.description")}
            </p>

            {/* Language Options */}
            <div className="space-y-3 mb-6">
              {languages.map((language) => (
                <motion.button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language.code)}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${
                    selectedLanguage === language.code
                      ? "border-vett-turquoise bg-vett-turquoise/5 shadow-md"
                      : "border-gray-200 dark:border-gray-600 hover:border-vett-turquoise/50 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{language.flag}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {language.name}
                    </span>
                  </div>
                  {selectedLanguage === language.code && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="text-vett-turquoise"
                    >
                      <Check className="w-5 h-5" />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <motion.button
                onClick={handleContinue}
                disabled={isLoading}
                className="w-full bg-vett-turquoise hover:bg-vett-dark-green text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{t("common.processing")}</span>
                  </div>
                ) : (
                  t("languageSelection.buttons.continue")
                )}
              </motion.button>

              <motion.button
                onClick={handleSkip}
                disabled={isLoading}
                className="w-full text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium py-2 px-6 rounded-xl transition-colors duration-200 disabled:opacity-50"
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
              >
                {t("languageSelection.buttons.skip")}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LanguageSelectionModal;
